import { Arg, Args, Ctx, FieldResolver, Query, Resolver, Root } from 'type-graphql'

import { Appliance, User } from '@things-factory/auth-base'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { Scenario } from '@things-factory/integration-base'

import { DataSet } from '../data-set/data-set.js'
import { DataSensor } from './data-sensor.js'
import { DataSensorList } from './data-sensor-type.js'

@Resolver(DataSensor)
export class DataSensorQuery {
  @Query(returns => DataSensor, {
    description:
      'Retrieves a single data sensor by its unique identifier. This query fetches detailed information about a sensor including its device configuration, data collection settings, and associated dataset information.'
  })
  async dataSensor(@Arg('id') id: string, @Ctx() context: ResolverContext): Promise<DataSensor> {
    const { domain } = context.state

    return await getRepository(DataSensor).findOne({
      where: { domain: { id: domain.id }, id }
    })
  }

  @Query(returns => DataSensorList, {
    description:
      'Retrieves multiple data sensors with pagination and filtering support. This query allows searching sensors by name, description, device ID, and tag, and is used for listing and managing automated data collection devices across all datasets.'
  })
  async dataSensors(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<DataSensorList> {
    const { domain } = context.state

    const queryBuilder = getQueryBuilderFromListParams({
      repository: getRepository(DataSensor),
      params,
      domain,
      searchables: ['name', 'description', 'deviceId', 'tag']
    })

    const [items, total] = await queryBuilder.getManyAndCount()

    return { items, total }
  }

  @FieldResolver(type => Appliance, {
    description:
      'Retrieves the appliance (device) associated with this data sensor. This field resolver provides access to the hardware device configuration and settings that the sensor is connected to.'
  })
  async appliance(@Root() dataSensor: DataSensor): Promise<Appliance> {
    return dataSensor.applianceId && (await getRepository(Appliance).findOneBy({ id: dataSensor.applianceId }))
  }

  @FieldResolver(type => DataSet, {
    description:
      'Retrieves the dataset that this data sensor is configured to collect data for. This field resolver provides access to the dataset configuration and settings associated with the sensor.'
  })
  async dataSet(@Root() dataSensor: DataSensor): Promise<DataSet> {
    return dataSensor.dataSetId && (await getRepository(DataSet).findOneBy({ id: dataSensor.dataSetId }))
  }

  @FieldResolver(type => Scenario, {
    description:
      'Retrieves the decoder scenario associated with this data sensor. This field resolver provides access to the data processing and transformation logic that converts raw sensor data into structured format.'
  })
  async decoder(@Root() dataSensor: DataSensor): Promise<Scenario> {
    return (
      dataSensor.decoder ||
      (dataSensor.decoderId &&
        (await getRepository(Scenario).findOneBy({
          id: dataSensor.decoderId
        })))
    )
  }

  @FieldResolver(type => Domain, {
    description:
      'Retrieves the domain that this data sensor belongs to. This field resolver provides access to the organizational context and settings associated with the sensor.'
  })
  async domain(@Root() dataSensor: DataSensor): Promise<Domain> {
    return dataSensor.domainId && (await getRepository(Domain).findOneBy({ id: dataSensor.domainId }))
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who last updated this data sensor. This field resolver identifies the person responsible for the most recent modifications to the sensor configuration.'
  })
  async updater(@Root() dataSensor: DataSensor): Promise<User> {
    return dataSensor.updaterId && (await getRepository(User).findOneBy({ id: dataSensor.updaterId }))
  }

  @FieldResolver(type => User, {
    description:
      'Retrieves the user who created this data sensor. This field resolver identifies the person responsible for initially configuring the sensor and its data collection settings.'
  })
  async creator(@Root() dataSensor: DataSensor): Promise<User> {
    return dataSensor.creatorId && (await getRepository(User).findOneBy({ id: dataSensor.creatorId }))
  }
}
